import type { CommandPaletteGroup, CommandPaletteItem, NavigationMenuItem } from '@nuxt/ui'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { can } from '@/libs/auth'
import { useDashboardFeed } from '@/libs/dashboard'
import { useAppStore } from '@/stores/app'
import { useFleetStore } from '@/stores/fleet'

/**
 * Layout state shared by `Default.vue` and its components (vue-spec `structure.md` —
 * `layouts/layout.ts`). Module-level, because there is one chrome per window: two copies of
 * "is the sidebar open" is how a menu closes itself on one component and not the other.
 *
 * Dark mode is deliberately NOT here, and it is not the topbar's either. Nuxt UI auto-registers
 * VueUse's `useDark`, and `stores/app.ts` wraps that SAME state as `colorMode` — one source of
 * truth, per vue-spec `theme.md`. The topbar only renders `ColorModeToggle`; duplicating the
 * state here would be the second source of truth theme.md forbids.
 */
/**
 * THE ADMINISTRATION MENU, in one place. `Sidebar.vue` renders it and `Topbar.vue` reads the
 * CURRENT entry's label for the page title the Nuxt UI dashboard navbar shows at the left —
 * two components, one list, so a section can never be named one thing in the menu and another
 * in the title bar. It lists only routes that EXIST.
 *
 * `routes` is every route of a section, so a DETAIL page (a user, a client) keeps its section
 * highlighted in the menu and titled in the navbar. The labels are i18n KEYS, not prose: the
 * strings stay in `libs/i18n/locales/*`.
 */
export interface NavItem {
    /** The route this entry navigates to. */
    name:     string
    /** The i18n key of its label (`nav.*`). */
    labelKey: string
    icon:     string
    /** Every route of this section. */
    routes:   string[]
    /**
     * A COSMETIC gate (§9.6): the entry is not drawn unless this answers `true`. Every endpoint
     * behind a gated entry is enforced server-side whether or not the row exists; this only
     * keeps the menu from offering a screen that would 403 on its first request.
     */
    gate?:    () => boolean
}

/**
 * A SECTION of the menu. `labelKey` is the small uppercase header above the group, or `null`
 * for the opening group — the landing page needs no heading, and one on its own would only
 * push the first row away from the brand.
 */
export interface NavGroup {
    labelKey: string | null
    items:    NavItem[]
}

/**
 * THE MENU, IN GROUPS (§9.1, §16.1 D74). Rows in one undifferentiated column say nothing about
 * what the app is FOR; grouped under terse headers they say it in a word or two. List only
 * routes that EXIST.
 *
 * `Users` disappears entirely without `admin` or `user_manager` (§9.1, M10) — the `gate` above.
 * `Registry` carries no gate on purpose: a read-only system page every user who reaches the
 * shell may open — the guard has already sent zero-access accounts to /no-access, and the server
 * 403s the endpoints independently (§9.6).
 */
export const NAV_GROUPS: NavGroup[] = [
    {
        labelKey: null,
        items: [
            { name: 'dashboard', labelKey: 'nav.dashboard', icon: 'i-lucide-layout-dashboard', routes: ['dashboard'] },
            { name: 'issues',    labelKey: 'nav.issues',    icon: 'i-lucide-triangle-alert',   routes: ['issues'] },
        ],
    },
    {
        labelKey: 'nav.group_fleet',
        items: [
            {
                name:     'instances',
                labelKey: 'nav.instances',
                icon:     'i-lucide-server',
                routes:   [
                    'instances',
                    'instance-overview', 'instance-runtime', 'instance-groups', 'instance-snapshots',
                    'instance-commands', 'instance-certificates', 'instance-static-config',
                    'instance-access', 'instance-settings', 'instance-install',
                    'instance-group-new', 'instance-group',
                ],
            },
            { name: 'clients', labelKey: 'nav.clients', icon: 'i-lucide-building-2', routes: ['clients', 'client'] },
        ],
    },
    {
        labelKey: 'nav.group_system',
        items: [
            { name: 'registry', labelKey: 'nav.registry', icon: 'i-lucide-package', routes: ['registry'] },
            { name: 'users',    labelKey: 'nav.users',    icon: 'i-lucide-users',   routes: ['users', 'user'], gate: can.manageUsers },
        ],
    },
]

/** Every entry, flattened — what the topbar titles the page from. The groups are the model. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items)

/** The i18n key of the page title for a route, falling back to the product name. */
export const navTitleKey = (routeName: string): string =>
    NAV_ITEMS.find((item) => item.routes.includes(routeName))?.labelKey ?? 'brand.product_name'

/**
 * IS THE MOBILE DRAWER OPEN. Module-level, and the only copy — below 1024px the sidebar is not
 * a column any more but an overlay over the whole shell, and an overlay that two components
 * disagree about is an overlay that cannot be closed.
 *
 * It only ever MEANS anything on mobile. On the desktop the sidebar is rendered because it is
 * the layout's left column, not because this is `true`, which is why `useLayoutShell()` forces
 * it back to `false` the moment the window grows past the breakpoint: a flag left set by a
 * phone-width session would otherwise re-open the drawer the next time the window narrows.
 */
const menuOpen = ref(false)

const openMenu = (): void => {
    menuOpen.value = true
}

const closeMenu = (): void => {
    menuOpen.value = false
}

const toggleMenu = (): void => {
    menuOpen.value = !menuOpen.value
}

/**
 * The state and the ONE action per verb. Every component that can open or close the drawer —
 * the topbar's hamburger, the drawer's own X, a nav row, the backdrop — calls one of these
 * three; none of them writes `menuOpen` itself.
 */
export const useLayout = () => {
    const appStore = useAppStore()

    const isMobile = computed((): boolean => appStore.isMobile())

    return { menuOpen, isMobile, openMenu, closeMenu, toggleMenu }
}

/**
 * THE DRAWER'S BEHAVIOUR, wired once by `Default.vue` and nowhere else.
 *
 * A drawer that can be opened and not closed is what the shell shipped with, so the four ways
 * out live together here rather than one per component: Escape, a route change, growing past
 * the breakpoint, and — in the layout, because it is a paint and not a behaviour — the
 * backdrop. While it is open the body cannot scroll, or the page behind the overlay scrolls
 * under the thumb instead of the menu.
 *
 * The listener is on `document` for the whole life of the shell rather than added and removed
 * around `menuOpen`: one registration, no window where a key lands between the watcher and the
 * listener. It does nothing at all unless the drawer is open.
 */
export const useLayoutShell = () => {
    const { isMobile } = useLayout()
    const route        = useRoute()

    /** What `body` had before the drawer took it — restored verbatim, not blanked. */
    let bodyOverflow: string | null = null

    const lockBody = (): void => {
        if (bodyOverflow === null) {
            bodyOverflow = document.body.style.overflow
        }

        document.body.style.overflow = 'hidden'
    }

    const unlockBody = (): void => {
        if (bodyOverflow === null) {
            return
        }

        document.body.style.overflow = bodyOverflow
        bodyOverflow = null
    }

    const onKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape' && menuOpen.value) {
            closeMenu()
        }
    }

    // Growing past the breakpoint: the sidebar is the layout's own column again, so the flag
    // that made it an overlay has to go with it.
    watch(isMobile, (mobile): void => {
        if (!mobile) {
            closeMenu()
        }
    })

    // Navigating is the most common way out of the menu — the row that was tapped has already
    // done its job. `Sidebar.vue` closes on select too; this catches every other route change
    // (a redirect, the back button, a link inside the page).
    watch(() => route.path, (): void => {
        closeMenu()
    })

    watch([menuOpen, isMobile], ([open, mobile]): void => {
        if (open && mobile) {
            lockBody()
        } else {
            unlockBody()
        }
    })

    onMounted((): void => {
        document.addEventListener('keydown', onKeydown)
    })

    onBeforeUnmount((): void => {
        document.removeEventListener('keydown', onKeydown)
        unlockBody()
    })
}

/**
 * THE MENU AS `UNavigationMenu` ITEMS — `NAV_GROUPS` with the gates applied, the active row lit
 * and the Issues badge attached. `Sidebar.vue` renders it and the Cmd+K palette lists it, so the
 * two can never drift into offering different destinations.
 *
 * THE ACTIVE ROW IS A SOFT PRIMARY PLATE. The component's `pill` variant already colours an
 * active row's label AND its leading icon `text-primary` (its `color="primary"` default); what
 * it paints behind them is `before:bg-elevated`, a neutral plate. One class per item turns that
 * into `before:bg-primary/10` — appended AFTER the variant's own class, so tailwind-merge keeps
 * the later of the two `before:bg-*` utilities — and the row becomes the tinted plate the shell
 * uses everywhere else for "this one". `active` is set explicitly rather than left to the
 * router, so a DETAIL route (`/instances/:id/runtime`) keeps its section lit.
 *
 * # The Issues entry carries the alarm (§7.5, §9.1, W11.4)
 *
 * Alerting in this product is UI-only and explicitly has no browser notification, no sound and no
 * title-blinking — which would be external alerting by the back door. What is left is this badge,
 * so it has to be live on **every** screen rather than only on the dashboard: the count comes from
 * the shell's own `GET /dashboard` poll (`libs/dashboard.ts`), which is the one aggregated request
 * per tab per fifteen seconds §9.2 budgets, not a second one opened for the badge.
 *
 * It counts **open critical** issues, not all open ones. A number that includes warnings never
 * returns to zero on a real fleet — a certificate is always ten days out somewhere — and a badge
 * that is always lit is a badge nobody looks at. It is omitted at zero: an empty badge reading
 * "0" states an absence as if it were news.
 */
export const useNavigation = () => {
    const { t }  = useI18n()
    const route  = useRoute()

    const { critical } = useDashboardFeed()

    /** Per-entry live counts. Only Issues carries one today. */
    const badges: Record<string, () => number> = {
        issues: () => critical.value,
    }

    const groups = computed((): { labelKey: string | null, items: NavigationMenuItem[] }[] =>
        NAV_GROUPS
            .map((group) => ({
                labelKey: group.labelKey,
                items: group.items
                    .filter((item) => item.gate?.() ?? true)
                    .map((item): NavigationMenuItem => {
                        const active = item.routes.includes(String(route.name ?? ''))
                        const count  = badges[item.name]?.() ?? 0

                        return {
                            label:    t(item.labelKey),
                            icon:     item.icon,
                            to:       { name: item.name },
                            active,
                            class:    active ? 'before:bg-primary/10' : '',
                            onSelect: () => { closeMenu() },
                            ...(count > 0
                                ? { badge: { label: String(count), color: 'error' as const, variant: 'subtle' as const } }
                                : {}),
                        }
                    }),
            }))
            .filter((group) => group.items.length > 0))

    return { groups }
}

/**
 * IS THE Cmd+K PALETTE OPEN. Module-level like `menuOpen`, for the same reason: the topbar's
 * search button opens it and `SearchPalette.vue` renders it, and one flag two components agree
 * on is the only kind that closes.
 */
const searchOpen = ref(false)

const openSearch = (): void => {
    searchOpen.value = true
}

const closeSearch = (): void => {
    searchOpen.value = false
}

/**
 * The Cmd+K palette (§9.1), over the cached fleet — which is the motion the whole component
 * exists for: *"an alert says instance X is broken → get to instance X"*. A sidebar tree of 100+
 * instances is not navigable (sized for headroom, not for the answered 20–50 fleet — §1
 * decision 1, O-7), so search is the primary way into an instance and the list is the secondary
 * one.
 *
 * Instances come **first**, ahead of clients and navigation: typing three letters at 2 a.m. is
 * nearly always aimed at an instance, and Nuxt UI keeps group order as given.
 *
 * The instance's client rides along as `suffix`, because two customers really do both have a
 * `traefik-01`, and a palette that offers two identical rows has answered nothing. Only reachable
 * entries are offered — the navigation group is the sidebar's own gated list — because a palette
 * that lists a destination and then refuses to go there is worse than one that lists nothing.
 */
export const useSearch = () => {
    const { t }      = useI18n()
    const fleetStore = useFleetStore()
    const { groups } = useNavigation()

    const searchGroups = computed((): CommandPaletteGroup<CommandPaletteItem>[] => {
        const result: CommandPaletteGroup<CommandPaletteItem>[] = []

        if (fleetStore.instances.length > 0) {
            result.push({
                id:    'instances',
                label: t('nav.instances'),
                items: fleetStore.instances.map((instance) => ({
                    label:    instance.name,
                    suffix:   instance.client_name,
                    icon:     'i-lucide-server',
                    to:       { name: 'instance-overview', params: { instanceId: instance.id } },
                    onSelect: closeSearch,
                })),
            })
        }

        if (fleetStore.clients.length > 0) {
            result.push({
                id:    'clients',
                label: t('nav.clients'),
                items: fleetStore.clients.map((client) => ({
                    label:    client.name,
                    icon:     'i-lucide-building-2',
                    to:       { name: 'client', params: { clientId: client.id } },
                    onSelect: closeSearch,
                })),
            })
        }

        result.push({
            id:    'navigation',
            label: t('nav.navigation'),
            items: groups.value
                .flatMap((group) => group.items)
                .map((item) => ({ label: item.label, icon: item.icon, to: item.to, onSelect: closeSearch })),
        })

        return result
    })

    return { searchOpen, searchGroups, openSearch, closeSearch }
}
